import { useEffect, useState } from "react";
import {
  ApiClientError,
  addAmazonNegativeKeywords,
  fetchAmazonAdGroupTargets,
  fetchAmazonAdGroups,
  setAmazonTargetBid,
} from "../api/client";
import { Badge } from "./Badge";
import { ConfirmDialog } from "./ConfirmDialog";
import { StateBanner } from "./StateBanner";
import { campaignStatusMeta, yen } from "../lib/meta";
import type { AmazonAdGroup, AmazonNegativeMatchType, AmazonTarget, Campaign } from "../types/contract";

/**
 * Amazon 広告（2026-10-23 P1）: キャンペーン行の「ターゲットを見る」で開く展開パネル。
 *   広告グループ一覧（構造＋期間実績）→ 広告グループを選ぶとターゲット（キーワード/商品/自動・除外含む）を表示。
 *   操作: ターゲットの入札変更（確認ダイアログ→ POST /amazon/targets/:id/bid）、除外キーワード追加（SP のみ・1行1語・
 *   NEGATIVE_EXACT/NEGATIVE_PHRASE → POST /amazon/adgroups/:id/negative-keywords）。worker 側で許可リスト fail-closed・上限・監査。
 *   実績は Reporting v1 のターゲット日次（翌朝取込）。当日分は含まれない。
 */

type GroupsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: AmazonAdGroup[] };

type TargetsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: AmazonTarget[] };

interface Props {
  campaign: Campaign;
  range: { from: string; to: string };
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  KEYWORD: "キーワード",
  PRODUCT: "商品",
  PRODUCT_CATEGORY: "カテゴリ",
  AUTO: "自動",
  THEME: "テーマ",
  AUDIENCE: "オーディエンス",
  UNKNOWN: "不明",
};

const MATCH_LABELS: Record<string, string> = {
  EXACT: "完全一致",
  PHRASE: "フレーズ",
  BROAD: "部分一致",
  CLOSE_MATCH: "ほぼ一致",
  LOOSE_MATCH: "大まかな一致",
  SUBSTITUTES: "代替品",
  COMPLEMENTS: "補完商品",
  NEGATIVE_EXACT: "除外・完全一致",
  NEGATIVE_PHRASE: "除外・フレーズ",
};

function matchLabel(m: string | null): string {
  if (!m) return "—";
  return MATCH_LABELS[m] ?? m;
}

export function AmazonTargetsPanel({ campaign, range }: Props) {
  const product = campaign.amazon?.ad_product ?? null;
  const [groups, setGroups] = useState<GroupsState>({ status: "loading" });
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const [targets, setTargets] = useState<Record<string, TargetsState>>({});

  // 入札変更: 行内編集 → 確認 → POST
  const [bidEdit, setBidEdit] = useState<{ id: string; value: string } | null>(null);
  const [pendingBid, setPendingBid] = useState<{ target: AmazonTarget; value: number } | null>(null);
  const [bidBusy, setBidBusy] = useState(false);
  const [bidError, setBidError] = useState<string | null>(null);

  // 除外キーワード追加
  const [negText, setNegText] = useState("");
  const [negMatch, setNegMatch] = useState<AmazonNegativeMatchType>("NEGATIVE_EXACT");
  const [pendingNeg, setPendingNeg] = useState<{ group: AmazonAdGroup; keywords: string[] } | null>(null);
  const [negBusy, setNegBusy] = useState(false);
  const [negError, setNegError] = useState<string | null>(null);
  const [negResult, setNegResult] = useState<{ created: string[]; failed: Array<{ keyword: string; message: string }> } | null>(null);

  const loadGroups = () => {
    setGroups({ status: "loading" });
    fetchAmazonAdGroups(campaign.id, { range, product })
      .then((res) => setGroups({ status: "ok", data: res.ad_groups }))
      .catch((e: unknown) => setGroups({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  };

  const loadTargets = (groupId: string) => {
    setTargets((prev) => ({ ...prev, [groupId]: { status: "loading" } }));
    fetchAmazonAdGroupTargets(groupId, { range, product })
      .then((res) => setTargets((prev) => ({ ...prev, [groupId]: { status: "ok", data: res.targets } })))
      .catch((e: unknown) =>
        setTargets((prev) => ({ ...prev, [groupId]: { status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" } }))
      );
  };

  useEffect(() => {
    loadGroups();
    setOpenGroupId(null);
    setTargets({});
    setNegResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id, range.from, range.to]);

  const toggleGroup = (g: AmazonAdGroup) => {
    if (openGroupId === g.id) {
      setOpenGroupId(null);
      return;
    }
    setOpenGroupId(g.id);
    setNegText("");
    setNegResult(null);
    setNegError(null);
    loadTargets(g.id);
  };

  const submitBidEdit = (t: AmazonTarget) => {
    if (!bidEdit) return;
    const value = Number(bidEdit.value.replace(/[,，¥\s]/g, ""));
    if (!Number.isInteger(value) || value < 2) {
      setBidError("入札は2円以上の整数で入力してください。");
      return;
    }
    setBidError(null);
    setPendingBid({ target: t, value });
  };

  const runBid = async () => {
    if (!pendingBid || !pendingBid.target.id) return;
    setBidBusy(true);
    setBidError(null);
    try {
      await setAmazonTargetBid(pendingBid.target.id, pendingBid.value, product);
      setPendingBid(null);
      setBidEdit(null);
      if (openGroupId) loadTargets(openGroupId);
    } catch (e) {
      setBidError(e instanceof ApiClientError ? e.message : "入札の変更に失敗しました。");
    } finally {
      setBidBusy(false);
    }
  };

  const openNegConfirm = (group: AmazonAdGroup) => {
    const keywords = Array.from(new Set(negText.split(/\r?\n|,|、/).map((s) => s.trim()).filter(Boolean)));
    if (keywords.length === 0) {
      setNegError("除外キーワードを1行に1語ずつ入力してください。");
      return;
    }
    if (keywords.length > 50) {
      setNegError("1回に追加できるのは50語までです。");
      return;
    }
    setNegError(null);
    setPendingNeg({ group, keywords });
  };

  const runNeg = async () => {
    if (!pendingNeg) return;
    setNegBusy(true);
    setNegError(null);
    try {
      const res = await addAmazonNegativeKeywords(pendingNeg.group.id, pendingNeg.keywords, negMatch, product);
      setNegResult({ created: res.created.map((c) => c.keyword), failed: res.failed });
      setPendingNeg(null);
      setNegText("");
      loadTargets(pendingNeg.group.id);
    } catch (e) {
      setNegError(e instanceof ApiClientError ? e.message : "除外キーワードの追加に失敗しました。");
    } finally {
      setNegBusy(false);
    }
  };

  const openGroup = groups.status === "ok" && openGroupId ? groups.data.find((g) => g.id === openGroupId) ?? null : null;
  const openTargets = openGroupId ? targets[openGroupId] : undefined;
  const isSp = (openGroup?.ad_product ?? product) === "SPONSORED_PRODUCTS";

  return (
    <div>
      <div className="ads-panel-title">広告グループとターゲット</div>
      <div className="cell-sub" style={{ marginBottom: 8, whiteSpace: "normal" }}>
        実績は Amazon 計測のターゲット日次レポート（翌朝取込・当日分は含まれません）。入札と除外キーワードは worker の許可リストに入ったキャンペーンのみ操作できます。
      </div>

      {groups.status === "loading" && (
        <div className="table-wrap">
          <div style={{ padding: 16 }} className="skeleton">
            &nbsp;
          </div>
        </div>
      )}
      {groups.status === "error" && <StateBanner kind="error" message={groups.message} onRetry={loadGroups} />}
      {groups.status === "ok" && groups.data.length === 0 && <div className="empty-state">広告グループはありません。</div>}
      {groups.status === "ok" && groups.data.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>広告グループ</th>
                <th>ステータス</th>
                <th>既定入札</th>
                <th>広告費</th>
                <th>clicks</th>
                <th>CTR</th>
                <th>CPC</th>
                <th>購入</th>
                <th>購入CPA</th>
                <th>売上</th>
                <th>ACOS</th>
                <th>ターゲット</th>
              </tr>
            </thead>
            <tbody>
              {groups.data.map((g) => {
                const st = campaignStatusMeta(g.status);
                return (
                  <tr key={g.id}>
                    <td style={{ whiteSpace: "normal", minWidth: 180 }}>{g.name}</td>
                    <td>
                      <Badge label={st.label} color={st.c} bg={st.bg} />
                    </td>
                    <td>{g.default_bid != null ? yen(g.default_bid) : "—"}</td>
                    <td>{yen(g.spend)}</td>
                    <td>{g.clicks.toLocaleString("ja-JP")}</td>
                    <td>{g.ctr}</td>
                    <td>{g.cpc != null ? yen(g.cpc) : "—"}</td>
                    <td>{g.purchases.toLocaleString("ja-JP")}件</td>
                    <td>{g.cpa_purchase != null ? yen(g.cpa_purchase) : "—"}</td>
                    <td>{g.sales > 0 ? yen(g.sales) : "—"}</td>
                    <td>{g.acos != null ? `${g.acos.toFixed(1)}%` : "—"}</td>
                    <td>
                      <button className="btn btn-sm" onClick={() => toggleGroup(g)}>
                        {openGroupId === g.id ? "閉じる" : "ターゲットを見る"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openGroup && (
        <div className="amz-panel-section">
          <div className="amz-panel-subtitle">
            <span>ターゲット: {openGroup.name}</span>
            {!openGroup.operable && <Badge label="操作対象外（許可リスト外）" color="var(--ink-sub)" bg="var(--ch-all-bg)" />}
          </div>
          {(!openTargets || openTargets.status === "loading") && (
            <div className="table-wrap">
              <div style={{ padding: 16 }} className="skeleton">
                &nbsp;
              </div>
            </div>
          )}
          {openTargets?.status === "error" && <StateBanner kind="error" message={openTargets.message} onRetry={() => loadTargets(openGroup.id)} />}
          {openTargets?.status === "ok" && openTargets.data.length === 0 && <div className="empty-state">ターゲットはありません。</div>}
          {openTargets?.status === "ok" && openTargets.data.length > 0 && (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ターゲット</th>
                    <th>種別</th>
                    <th>マッチ</th>
                    <th>ステータス</th>
                    <th>入札</th>
                    <th>広告費</th>
                    <th>clicks</th>
                    <th>CTR</th>
                    <th>CPC</th>
                    <th>購入</th>
                    <th>購入CPA</th>
                    <th>ACOS</th>
                  </tr>
                </thead>
                <tbody>
                  {openTargets.data.map((t, i) => {
                    const st = campaignStatusMeta(t.status);
                    const key = t.id ?? `orphan-${i}`;
                    return (
                      <tr key={key}>
                        <td className="amz-expr">
                          {t.expression || "—"}
                          {t.negative && (
                            <>
                              {" "}
                              <Badge label="除外" color="var(--st-pending-c)" bg="var(--st-pending-bg)" />
                            </>
                          )}
                          {t.id === null && <span className="cell-sub">（実績のみ・構造に無し）</span>}
                        </td>
                        <td>{TARGET_TYPE_LABELS[t.target_type] ?? t.target_type}</td>
                        <td>{matchLabel(t.match_type)}</td>
                        <td>
                          <Badge label={st.label} color={st.c} bg={st.bg} />
                        </td>
                        <td>
                          {t.operable && t.id && bidEdit?.id === t.id ? (
                            <span className="budget-edit">
                              <input
                                type="text"
                                inputMode="numeric"
                                value={bidEdit.value}
                                aria-label="入札（円）"
                                onChange={(e) => setBidEdit({ id: t.id as string, value: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") submitBidEdit(t);
                                  if (e.key === "Escape") setBidEdit(null);
                                }}
                                autoFocus
                              />
                              <button className="btn btn-sm" onClick={() => submitBidEdit(t)}>
                                保存
                              </button>
                              <button className="link-btn" onClick={() => { setBidEdit(null); setBidError(null); }}>
                                取消
                              </button>
                              {bidError && <span className="ad-card-copy-failed">{bidError}</span>}
                            </span>
                          ) : (
                            <span className="budget-cell">
                              {t.bid != null ? yen(t.bid) : "—"}
                              {t.operable && t.id && (
                                <button
                                  className="link-btn"
                                  onClick={() => { setBidError(null); setBidEdit({ id: t.id as string, value: String(t.bid ?? "") }); }}
                                  title="入札を変更（Amazon広告のターゲット入札を書き換えます）"
                                >
                                  変更
                                </button>
                              )}
                            </span>
                          )}
                        </td>
                        <td>{yen(t.spend)}</td>
                        <td>{t.clicks.toLocaleString("ja-JP")}</td>
                        <td>{t.ctr}</td>
                        <td>{t.cpc != null ? yen(t.cpc) : "—"}</td>
                        <td>{t.purchases.toLocaleString("ja-JP")}件</td>
                        <td>{t.cpa_purchase != null ? yen(t.cpa_purchase) : "—"}</td>
                        <td>{t.acos != null ? `${t.acos.toFixed(1)}%` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {openGroup.operable && isSp && (
            <div className="amz-neg-form">
              <div className="amz-panel-subtitle">除外キーワードを追加（Sponsored Products・広告グループ単位）</div>
              <textarea
                placeholder={"1行に1語（例）\n無料\n中古\nプラスチック"}
                value={negText}
                onChange={(e) => setNegText(e.target.value)}
                aria-label="除外キーワード"
              />
              <div className="amz-neg-form-row">
                <select value={negMatch} onChange={(e) => setNegMatch(e.target.value as AmazonNegativeMatchType)} aria-label="マッチタイプ">
                  <option value="NEGATIVE_EXACT">除外・完全一致</option>
                  <option value="NEGATIVE_PHRASE">除外・フレーズ一致</option>
                </select>
                <button className="btn btn-sm" onClick={() => openNegConfirm(openGroup)}>
                  追加する
                </button>
                {negError && !pendingNeg && <span className="ad-card-copy-failed">{negError}</span>}
              </div>
              {negResult && (
                <div className="cell-sub" style={{ whiteSpace: "normal" }}>
                  追加 {negResult.created.length}語{negResult.failed.length > 0 ? `・失敗 ${negResult.failed.length}語` : ""}
                  {negResult.failed.length > 0 && (
                    <ul className="amz-result-list">
                      {negResult.failed.map((f) => (
                        <li key={f.keyword}>
                          {f.keyword}: {f.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
          {openGroup.operable && !isSp && (
            <div className="cell-sub" style={{ marginTop: 8, whiteSpace: "normal" }}>
              除外キーワードの追加はコンソールでは Sponsored Products のみ対応です（SB/SD は Amazon 広告コンソールで）。
            </div>
          )}
        </div>
      )}

      {pendingBid && (
        <ConfirmDialog
          title="入札を変更しますか？"
          description={`「${pendingBid.target.expression}」（${matchLabel(pendingBid.target.match_type)}）の入札を ${
            pendingBid.target.bid != null ? yen(pendingBid.target.bid) : "—"
          } → ${yen(pendingBid.value)} に変更します。Amazon 広告のターゲット入札を書き換えます。`}
          confirmLabel="変更する"
          danger={pendingBid.target.bid != null && pendingBid.value > pendingBid.target.bid * 2}
          onConfirm={runBid}
          onCancel={() => setPendingBid(null)}
          busy={bidBusy}
          errorMessage={bidError}
        />
      )}

      {pendingNeg && (
        <ConfirmDialog
          title="除外キーワードを追加しますか？"
          description={`「${pendingNeg.group.name}」に ${pendingNeg.keywords.length} 語（${negMatch === "NEGATIVE_EXACT" ? "除外・完全一致" : "除外・フレーズ一致"}）を追加します: ${pendingNeg.keywords.join(" / ")}。追加後にこれらの検索語では広告が出なくなります（取り消しは Amazon 広告コンソールで）。`}
          confirmLabel="追加する"
          danger
          onConfirm={runNeg}
          onCancel={() => setPendingNeg(null)}
          busy={negBusy}
          errorMessage={negError}
        />
      )}
    </div>
  );
}
